'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check, Search, X, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useScrollLock } from '@/lib/hooks';
import { useAnchoredPanel } from '@/lib/hooks/useAnchoredPanel';
import type { SpecialtyOption } from '@/lib/specialties/types';
import { groupLabel } from '@/lib/specialties/groups';
import { searchSpecialties, groupSpecialties, specialtyLabel } from '@/lib/specialties/search';

export interface SpecialtyPickerProps {
  value: string | null; onChange: (id: string | null) => void;
  options: SpecialtyOption[]; exclude?: string[];
  label?: string; placeholder?: string; required?: boolean; error?: string; disabled?: boolean; id?: string;
}

/** Below Tailwind's `sm`: the list opens full-screen instead of as a dropdown. */
const PHONE_QUERY = '(max-width: 639px)';

/**
 * On a 320 px phone the dropdown showed a 288 px window onto ~215 rows, and
 * «Реабілітація» sat ~170 rows down (2026-10-24). On a phone the list now
 * opens full-screen; group chips jump to a group; the single «Інша
 * спеціальність» row is always at the end, also when the search finds nothing.
 */
export default function SpecialtyPicker({ value, onChange, options, exclude = [], label, placeholder, required, error, disabled, id }: SpecialtyPickerProps) {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [sheet, setSheet] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const dropdown = useAnchoredPanel(anchorRef, 420);

  useScrollLock(isOpen && sheet);

  const open = () => {
    const phone = typeof window !== 'undefined' && window.matchMedia?.(PHONE_QUERY).matches === true;
    setSheet(phone);
    setIsOpen(true);
    if (!phone) dropdown.open();
  };
  const closeDropdown = dropdown.close;
  const close = () => { setIsOpen(false); setQuery(''); closeDropdown(); };

  useEffect(() => {
    if (!isOpen) return;
    // On a phone the keyboard would cover half the list; the search box is one tap away.
    if (!sheet) searchRef.current?.focus();
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setIsOpen(false);
      setQuery('');
      closeDropdown();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [isOpen, sheet, closeDropdown]);

  const regular = useMemo(() => options.filter(o => !o.isOther), [options]);
  const other = useMemo(
    () => options.find(o => o.isOther && o.groupKey === 'other') ?? options.find(o => o.isOther) ?? null,
    [options]
  );
  const visible = useMemo(() => searchSpecialties(regular, query, language, exclude), [regular, query, language, exclude]);
  const grouped = useMemo(() => (query ? null : groupSpecialties(visible, language)), [visible, query, language]);
  const selected = options.find(o => o.id === value) ?? null;
  const triggerText = selected ? specialtyLabel(selected, language) : (placeholder ?? t('profile.selectSpecialty'));
  const groupAnchor = (key: string) => `${id ?? 'specialty'}-group-${key}`;

  const jumpTo = (key: string) => {
    const list = listRef.current;
    const header = list?.querySelector<HTMLElement>(`[data-group="${key}"]`);
    // Instant: a smooth scroll over ~170 rows is still moving when the person looks.
    if (list && header) list.scrollTop = header.offsetTop;
  };

  const choose = (optionId: string) => { onChange(optionId); close(); };

  const renderOption = (o: SpecialtyOption, showGroup: boolean) => {
    const isSelected = o.id === value;
    return (
      <Button key={o.id} type="button" variant="ghost" role="option" aria-selected={isSelected} data-selected={isSelected}
        onClick={() => choose(o.id)}
        className={`w-full h-auto min-h-11 text-left justify-start rounded-none px-3 sm:px-4 py-2 flex items-start gap-2 text-sm ${isSelected ? 'bg-blue-50 text-blue-700 hover:bg-blue-50' : 'text-gray-900 hover:bg-gray-50 font-normal'}`}>
        <span className="flex-1 min-w-0">
          <span className="block break-words whitespace-normal leading-snug">{specialtyLabel(o, language)}</span>
          {showGroup && <span className="block text-xs text-gray-600 whitespace-normal">{groupLabel(o.groupKey, language)}</span>}
        </span>
        {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5" />}
      </Button>
    );
  };

  const otherRow = other && !exclude.includes(other.id) && (
    <Button type="button" variant="ghost" role="option" aria-selected={other.id === value} data-selected={other.id === value}
      onClick={() => choose(other.id)}
      className={`w-full h-auto min-h-11 text-left justify-start rounded-none px-3 sm:px-4 py-3 flex items-center gap-2 text-sm border-t border-gray-200 ${other.id === value ? 'bg-blue-50 text-blue-700 hover:bg-blue-50' : 'text-blue-700 hover:bg-blue-50 font-medium'}`}>
      <PenLine className="h-4 w-4 flex-shrink-0" />
      <span className="flex-1 min-w-0 whitespace-normal">{t('profile.otherSpecialtyManual')}</span>
      {other.id === value && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
    </Button>
  );

  const searchBox = (
    <div className="bg-white border-b border-gray-200 p-2">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
        <input ref={searchRef} type="search" role="searchbox" value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key !== 'Escape') return;
            // Keep the Escape here: a dialog around the picker would close too.
            e.stopPropagation();
            if (query) setQuery(''); else close();
          }}
          placeholder={t('profile.searchSpecialty')} autoComplete="off"
          className="w-full pl-8 pr-3 py-2 text-base sm:text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50" />
      </div>
    </div>
  );

  const chips = grouped && grouped.length > 1 && (
    <div className="flex gap-1.5 overflow-x-auto px-2 py-2 border-b border-gray-200 bg-white" aria-label={t('profile.specialtyGroups')}>
      {grouped.map(g => (
        <button key={g.key} type="button" onClick={() => jumpTo(g.key)}
          className="flex-shrink-0 whitespace-nowrap rounded-full border border-gray-300 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50">
          {g.label}
        </button>
      ))}
    </div>
  );

  const list = (
    <>
      {visible.length === 0 && <div className="px-4 py-3 text-sm text-gray-500 text-center">{t('profile.noSpecialtyFound')}</div>}
      {grouped ? grouped.map(g => (
        <div key={g.key} data-group={g.key}>
          <div id={groupAnchor(g.key)}
            className="sticky top-0 z-10 cursor-default select-none px-3 sm:px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 bg-gray-100 border-y border-gray-200">
            {g.label}
          </div>
          {g.items.map(o => renderOption(o, false))}
        </div>
      )) : visible.map(o => renderOption(o, true))}
      {otherRow}
    </>
  );

  const panel = sheet ? (
    <div ref={panelRef} role="dialog" aria-modal="true" aria-label={label ?? t('profile.selectSpecialty')}
      className="fixed inset-0 z-[70] flex flex-col bg-white">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-200">
        <span className="flex-1 min-w-0 text-base font-semibold text-gray-900 break-words">{label ?? t('profile.selectSpecialty')}</span>
        <button type="button" onClick={close} aria-label={t('common.close')} className="p-2 -m-2 text-gray-500 hover:text-gray-700">
          <X className="h-5 w-5" />
        </button>
      </div>
      {searchBox}
      {chips}
      <div ref={listRef} role="listbox" className="relative flex-1 overflow-y-auto overscroll-contain">{list}</div>
    </div>
  ) : (
    <div ref={panelRef} style={dropdown.style ?? undefined} className="z-[80] flex flex-col bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
      <div className="shrink-0">{searchBox}</div>
      <div className="shrink-0">{chips}</div>
      <div ref={listRef} role="listbox" className="relative min-h-0 flex-1 overflow-y-auto overscroll-contain">{list}</div>
    </div>
  );

  return (
    <div id={id} className="min-w-0" ref={wrapperRef}>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1 break-words">{label}{required && <span className="text-red-500">*</span>}</label>}
      <div className="relative" ref={anchorRef}>
        <Button type="button" variant="outline" disabled={disabled} onClick={() => { if (disabled) return; if (isOpen) close(); else open(); }}
          aria-haspopup="listbox" aria-expanded={isOpen}
          className={`w-full h-10 text-left justify-start px-4 py-2 pr-12 border ${error ? 'border-red-500' : 'border-gray-300'} bg-white ${value ? 'text-gray-900' : 'text-gray-500'} text-sm font-normal truncate hover:border-gray-400 hover:bg-white`}>
          {triggerText}
        </Button>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className={`h-4 w-4 text-gray-500 transition-all ${isOpen ? 'rotate-180' : ''}`} />
        </div>
        {/* Both are portalled: a dialog's transformed panel would pin `fixed` to itself, and a
            scrolling form would clip a dropdown inside it. */}
        {isOpen && (sheet || dropdown.style) && createPortal(panel, document.body)}
      </div>
      {error && <p className="mt-1 text-xs sm:text-sm text-red-600 break-words" role="alert">{error}</p>}
    </div>
  );
}
